import React, { useEffect, useState, useCallback } from "react";
import { Link2, Plus, Trash2, Loader2, AlertCircle, ExternalLink, ChevronDown } from "lucide-react";
import { Button } from "../ui/button";
import crmService from "../../services/crm.service";

const ENTITY_LABELS = {
  sindicato: { single: "Space", criar: "Criar Space", vincular: "Vincular Space", hint: "ID do Space no ClickUp" },
  processo:  { single: "Lista", criar: "Criar Lista",  vincular: "Vincular Lista",  hint: "ID da Lista no ClickUp"  },
};

function clickupUrl(entityType, id, teamId) {
  if (entityType === "sindicato") return teamId ? `https://app.clickup.com/${teamId}/v/s/${id}` : null;
  if (entityType === "processo")  return teamId ? `https://app.clickup.com/${teamId}/v/b/li/${id}` : null;
  return null;
}

function MappingRow({ mapping, entityType, teamId, onDelete, deleting }) {
  const url = clickupUrl(entityType, mapping.external_id, teamId);
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-statusSuccess/20 bg-statusSuccessBg px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <Link2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
        <span className="text-xs font-semibold text-statusSuccess shrink-0 uppercase">{mapping.provider}</span>
        <code className="text-xs text-statusSuccess bg-statusSuccessBg px-1.5 py-0.5 rounded font-mono truncate">
          {mapping.external_id}
        </code>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center h-7 w-7 rounded text-emerald-500 hover:text-statusSuccess hover:bg-statusSuccessBg transition-colors"
            title="Abrir no CRM"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-red-300 hover:text-red-500 hover:bg-statusErrorBg"
          onClick={() => onDelete(mapping)}
          disabled={deleting === mapping.id}
          title="Remover vínculo"
        >
          {deleting === mapping.id
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <Trash2 className="h-3.5 w-3.5" />
          }
        </Button>
      </div>
    </div>
  );
}

export default function CrmEntityPanel({ entityType, entityId, onChanged }) {
  const labels = ENTITY_LABELS[entityType] || {};

  const [mappings, setMappings] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [teamId, setTeamId] = useState(null);

  const [linkProvider, setLinkProvider] = useState("");
  const [linkInput, setLinkInput] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [showCriarMenu, setShowCriarMenu] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, c, tid] = await Promise.all([
        entityType === "sindicato"
          ? crmService.getSindicatoMapping(entityId)
          : crmService.getProcessoMapping(entityId),
        crmService.getConfigs(),
        crmService.getTeamId().catch(() => null),
      ]);
      setMappings(Array.isArray(m) ? m : (m ? [m] : []));
      setConfigs((c || []).filter(cfg => cfg.is_active));
      if (tid) setTeamId(tid);
    } catch {
      setMappings([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  const linkedProviders = new Set(mappings.map(m => m.provider));
  const availableConfigs = configs.filter(c => !linkedProviders.has(c.name));

  const handleCriar = async (provider) => {
    setActionLoading(provider);
    setError(null);
    setShowCriarMenu(false);
    try {
      const fn = entityType === "sindicato"
        ? () => crmService.criarSindicatoSpace(entityId, provider)
        : () => crmService.criarProcessoList(entityId, provider);
      const res = await fn();
      if (res.success) {
        await load();
        onChanged?.();
      } else {
        setError(res.message || "Falha ao criar no CRM.");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao criar no CRM.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleLink = async () => {
    if (!linkInput.trim() || !linkProvider) return;
    setActionLoading("link");
    setError(null);
    try {
      const fn = entityType === "sindicato"
        ? () => crmService.linkSindicato(entityId, linkInput.trim(), linkProvider)
        : () => crmService.linkProcesso(entityId, linkInput.trim(), linkProvider);
      await fn();
      setLinkInput("");
      setShowLink(false);
      setLinkProvider("");
      await load();
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao vincular.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (mapping) => {
    if (!window.confirm("Remover vínculo CRM?")) return;
    setDeleting(mapping.id);
    setError(null);
    try {
      const fn = entityType === "sindicato"
        ? () => crmService.deleteSindicatoMapping(entityId, mapping.id)
        : () => crmService.deleteProcessoMapping(entityId, mapping.id);
      await fn();
      await load();
      onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Erro ao remover vínculo.");
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Carregando...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {mappings.length === 0 && (
        <p className="text-sm text-muted-foreground">Sem vínculo CRM.</p>
      )}

      {mappings.map(m => (
        <MappingRow
          key={m.id}
          mapping={m}
          entityType={entityType}
          teamId={teamId}
          onDelete={handleDelete}
          deleting={deleting}
        />
      ))}

      {availableConfigs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {availableConfigs.length === 1 ? (
            <Button
              size="sm"
              variant="outline"
              className="border-statusInfo/20 text-statusInfo hover:bg-statusInfoBg h-8 text-xs gap-1.5"
              onClick={() => handleCriar(availableConfigs[0].name)}
              disabled={!!actionLoading}
            >
              {actionLoading === availableConfigs[0].name
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : <Plus className="h-3 w-3" />
              }
              {labels.criar} no {availableConfigs[0].display_name}
            </Button>
          ) : (
            <div className="relative">
              <Button
                size="sm"
                variant="outline"
                className="border-statusInfo/20 text-statusInfo hover:bg-statusInfoBg h-8 text-xs gap-1.5"
                onClick={() => setShowCriarMenu(s => !s)}
                disabled={!!actionLoading}
              >
                {actionLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                {labels.criar}
                <ChevronDown className="h-3 w-3" />
              </Button>
              {showCriarMenu && (
                <div className="absolute left-0 top-9 z-10 bg-card border border-border rounded-lg shadow-md min-w-[160px] py-1">
                  {availableConfigs.map(cfg => (
                    <button
                      key={cfg.name}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-muted flex items-center gap-2"
                      onClick={() => handleCriar(cfg.name)}
                    >
                      <span>{cfg.icon || "🔌"}</span>
                      {cfg.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!showLink ? (
            <Button
              size="sm"
              variant="outline"
              className="border-border text-muted-foreground hover:bg-muted h-8 text-xs gap-1.5"
              onClick={() => {
                setLinkProvider(availableConfigs[0]?.name || "");
                setShowLink(true);
              }}
            >
              <Link2 className="h-3 w-3" />
              {labels.vincular}
            </Button>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              {availableConfigs.length > 1 && (
                <select
                  value={linkProvider}
                  onChange={e => setLinkProvider(e.target.value)}
                  className="h-8 text-xs border border-gray-300 rounded-md px-2 focus:outline-none focus:ring-1"
                >
                  {availableConfigs.map(c => (
                    <option key={c.name} value={c.name}>{c.display_name}</option>
                  ))}
                </select>
              )}
              <input
                type="text"
                value={linkInput}
                onChange={e => setLinkInput(e.target.value)}
                placeholder={labels.hint}
                className="h-8 text-xs border border-gray-300 rounded-md px-2.5 focus:outline-none focus:ring-1 w-44"
                onKeyDown={e => e.key === "Enter" && handleLink()}
                autoFocus
              />
              <Button
                size="sm"
                className="h-8 text-xs px-3"
                onClick={handleLink}
                disabled={!!actionLoading || !linkInput.trim() || !linkProvider}
              >
                {actionLoading === "link" ? <Loader2 className="h-3 w-3 animate-spin" /> : "Salvar"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs px-2 text-muted-foreground"
                onClick={() => { setShowLink(false); setLinkInput(""); }}
              >
                Cancelar
              </Button>
            </div>
          )}
        </div>
      )}

      {configs.length === 0 && mappings.length === 0 && (
        <p className="text-xs text-amber-600">Nenhum provedor CRM ativo configurado.</p>
      )}

      {error && (
        <div className="flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}
    </div>
  );
}
